import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Upload, Star, ChevronLeft, ChevronRight, Trash2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch, erroApi } from './api';

const VAZIO = {
  nome: '', sku: '', categoria: '', marca: '', descricao: '', detalhesTexto: '',
  precoVenda: '', precoPromocional: '', parcelasVezes: '', parcelasValor: '',
  estoqueCA: '', estoqueDU: '', estoqueMinimo: '2', status: 'ativo',
  mostrarEstoque: false, destaque: false, baseAtualizadoEm: null,
};

export default function ProdutoForm() {
  const { id } = useParams();
  const editando = Boolean(id);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [form, setForm] = useState(VAZIO);
  const [fotos, setFotos] = useState([]);
  const [novaCategoria, setNovaCategoria] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [subindo, setSubindo] = useState(false);
  const [carregado, setCarregado] = useState(!editando);

  const { data: cats } = useQuery({
    queryKey: ['admin-categorias'],
    queryFn: async () => (await apiFetch('/admin/categorias')).json(),
  });

  useEffect(() => {
    if (!editando) return;
    apiFetch(`/admin/produtos/${id}`).then(async (r) => {
      if (!r.ok) { toast.error('Produto não encontrado.'); navigate('/admin/produtos'); return; }
      const p = await r.json();
      setForm({
        nome: p.nome, sku: p.sku || '', categoria: p.categoria, marca: p.marca || '',
        descricao: p.descricao || '', detalhesTexto: (p.detalhes || []).join('\n'),
        precoVenda: String(p.precoVenda), precoPromocional: p.precoPromocional != null ? String(p.precoPromocional) : '',
        parcelasVezes: p.parcelas?.vezes ? String(p.parcelas.vezes) : '',
        parcelasValor: p.parcelas?.valor ? String(p.parcelas.valor) : '',
        estoqueCA: p.estoqueCerroAzul != null ? String(p.estoqueCerroAzul) : '',
        estoqueDU: p.estoqueDoutorUlysses != null ? String(p.estoqueDoutorUlysses) : '',
        estoqueMinimo: String(p.estoqueMinimo ?? 2), status: p.status,
        mostrarEstoque: Boolean(p.mostrarEstoque), destaque: Boolean(p.destaque),
        baseAtualizadoEm: p.atualizadoEm,
      });
      setFotos(p.fotos || []);
      setCarregado(true);
    });
  }, [id]);

  const set = (campo) => (e) => setForm({ ...form, [campo]: e.target.value });

  const enviarArquivos = async (lista) => {
    if (!lista?.length) return;
    setSubindo(true);
    const fd = new FormData();
    [...lista].forEach((f) => fd.append('files', f));
    const r = await apiFetch('/admin/upload', { method: 'POST', body: fd });
    setSubindo(false);
    if (r.ok) {
      const data = await r.json();
      setFotos((atual) => [...atual, ...data.files.map((f) => f.url)]);
      toast.success(`${data.files.length} foto(s) enviada(s).`);
    } else {
      toast.error(erroApi((await r.json().catch(() => ({}))).detail));
    }
    if (fileRef.current) fileRef.current.value = '';
  };

  const mover = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= fotos.length) return;
    const novo = [...fotos];
    [novo[i], novo[j]] = [novo[j], novo[i]];
    setFotos(novo);
  };

  const principal = (i) => {
    const novo = [...fotos];
    const [foto] = novo.splice(i, 1);
    setFotos([foto, ...novo]);
    toast.success('Foto principal definida.');
  };

  const salvar = async (e) => {
    e.preventDefault();
    if (fotos.length === 0) { toast.error('Adicione pelo menos uma foto do produto.'); return; }
    let categoriaId = form.categoria;
    if (categoriaId === '__nova__') {
      if (novaCategoria.trim().length < 2) { toast.error('Informe o nome da nova categoria.'); return; }
      const rc = await apiFetch('/admin/categorias', { method: 'POST', body: JSON.stringify({ nome: novaCategoria.trim() }) });
      if (!rc.ok) { toast.error(erroApi((await rc.json().catch(() => ({}))).detail)); return; }
      categoriaId = (await rc.json()).id;
      qc.invalidateQueries({ queryKey: ['admin-categorias'] });
    }
    const parcelas = form.parcelasVezes && form.parcelasValor
      ? { vezes: parseInt(form.parcelasVezes, 10), valor: parseFloat(form.parcelasValor.replace(',', '.')) }
      : null;
    const payload = {
      nome: form.nome.trim(),
      sku: form.sku.trim(),
      categoria: categoriaId,
      marca: form.marca.trim() || null,
      descricao: form.descricao.trim(),
      detalhes: form.detalhesTexto.split('\n').map((d) => d.trim()).filter(Boolean),
      precoVenda: parseFloat(form.precoVenda.replace(',', '.')),
      precoPromocional: form.precoPromocional ? parseFloat(form.precoPromocional.replace(',', '.')) : null,
      parcelas,
      estoqueCerroAzul: form.estoqueCA === '' ? null : parseInt(form.estoqueCA, 10),
      estoqueDoutorUlysses: form.estoqueDU === '' ? null : parseInt(form.estoqueDU, 10),
      estoqueMinimo: parseInt(form.estoqueMinimo, 10) || 0,
      mostrarEstoque: form.mostrarEstoque,
      destaque: form.destaque,
      baseAtualizadoEm: form.baseAtualizadoEm,
      status: form.status,
      fotos,
    };
    setEnviando(true);
    const r = await apiFetch(editando ? `/admin/produtos/${id}` : '/admin/produtos', {
      method: editando ? 'PUT' : 'POST',
      body: JSON.stringify(payload),
    });
    setEnviando(false);
    if (r.ok) {
      toast.success(editando ? 'Produto atualizado!' : 'Produto cadastrado! Já está no catálogo.');
      qc.invalidateQueries({ queryKey: ['admin-produtos'] });
      qc.invalidateQueries({ queryKey: ['admin-dashboard'] });
      qc.invalidateQueries({ queryKey: ['catalogo'] });
      navigate('/admin/produtos');
    } else if (r.status === 409) {
      toast.error(erroApi((await r.json().catch(() => ({}))).detail));
    } else {
      toast.error(erroApi((await r.json().catch(() => ({}))).detail));
    }
  };

  if (!carregado) return <p className="text-sm text-slate-400">Carregando...</p>;

  const inputCls = 'mt-1 w-full rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm outline-none focus:border-mk-blue focus:ring-2 focus:ring-mk-blue/20';
  const labelCls = 'text-xs font-bold uppercase tracking-wide text-slate-500';

  return (
    <div data-testid="admin-product-form" className="max-w-3xl">
      <h1 className="font-heading text-2xl font-extrabold text-slate-900">{editando ? 'Editar produto' : 'Cadastrar produto'}</h1>
      <form onSubmit={salvar} className="mt-5 space-y-5">
        <div className="rounded-2xl bg-white p-5 shadow-sm space-y-4">
          <div>
            <label className={labelCls}>Nome do produto *</label>
            <input data-testid="form-nome" required minLength={2} value={form.nome} onChange={set('nome')} className={inputCls} placeholder="Ex.: Geladeira Consul 333L Frost Free" />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Código / SKU</label>
              <input data-testid="form-sku" value={form.sku} onChange={set('sku')} className={inputCls} placeholder="Opcional" />
            </div>
            <div>
              <label className={labelCls}>Marca</label>
              <input data-testid="form-marca" value={form.marca} onChange={set('marca')} className={inputCls} placeholder="Ex.: Consul" />
            </div>
          </div>
          <div>
            <label className={labelCls}>Categoria *</label>
            <select data-testid="form-categoria" required value={form.categoria} onChange={set('categoria')} className={inputCls}>
              <option value="">Selecione...</option>
              {(cats || []).map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
              <option value="__nova__">+ Nova categoria</option>
            </select>
            {form.categoria === '__nova__' && (
              <input data-testid="form-nova-categoria" value={novaCategoria} onChange={(e) => setNovaCategoria(e.target.value)} className={inputCls} placeholder="Nome da nova categoria" />
            )}
          </div>
          <div>
            <label className={labelCls}>Descrição</label>
            <textarea data-testid="form-descricao" rows={2} value={form.descricao} onChange={set('descricao')} className={inputCls} placeholder="Descrição breve do produto" />
          </div>
          <div>
            <label className={labelCls}>Detalhes (um por linha)</label>
            <textarea data-testid="form-detalhes" rows={3} value={form.detalhesTexto} onChange={set('detalhesTexto')} className={inputCls} placeholder={'Capacidade: 333 litros\nFrost Free'} />
          </div>
        </div>

        <div className="rounded-2xl bg-white p-5 shadow-sm space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Preço de venda (à vista) *</label>
              <input data-testid="form-preco" required type="number" min="0" step="0.01" value={form.precoVenda} onChange={set('precoVenda')} className={inputCls} placeholder="0,00" />
            </div>
            <div>
              <label className={labelCls}>Preço promocional</label>
              <input data-testid="form-preco-promo" type="number" min="0" step="0.01" value={form.precoPromocional} onChange={set('precoPromocional')} className={inputCls} placeholder="Opcional" />
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Parcelas (vezes)</label>
              <input data-testid="form-parcelas-vezes" type="number" min="1" max="24" value={form.parcelasVezes} onChange={set('parcelasVezes')} className={inputCls} placeholder="Ex.: 12" />
            </div>
            <div>
              <label className={labelCls}>Valor da parcela</label>
              <input data-testid="form-parcelas-valor" type="number" min="0" step="0.01" value={form.parcelasValor} onChange={set('parcelasValor')} className={inputCls} placeholder="Ex.: 299,90" />
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Estoque — Loja Cerro Azul</label>
              <input data-testid="form-estoque-ca" type="number" min="0" value={form.estoqueCA} onChange={set('estoqueCA')} className={inputCls} placeholder="Vazio = não controlado" />
            </div>
            <div>
              <label className={labelCls}>Estoque — Loja Doutor Ulysses</label>
              <input data-testid="form-estoque-du" type="number" min="0" value={form.estoqueDU} onChange={set('estoqueDU')} className={inputCls} placeholder="Vazio = não controlado" />
            </div>
          </div>
          <p className="rounded-xl bg-blue-50 px-4 py-2.5 text-xs font-semibold text-mk-blue">
            Estoque total calculado automaticamente: {`${
              (parseInt(form.estoqueCA, 10) || 0) + (parseInt(form.estoqueDU, 10) || 0)
            }`} unidades (soma das duas lojas). Para movimentações, use a tela Controle de estoque.
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Estoque mínimo</label>
              <input data-testid="form-estoque-min" type="number" min="0" value={form.estoqueMinimo} onChange={set('estoqueMinimo')} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Situação</label>
              <select data-testid="form-status" value={form.status} onChange={set('status')} className={inputCls}>
                <option value="ativo">Ativo</option>
                <option value="inativo">Inativo</option>
                <option value="esgotado">Esgotado</option>
              </select>
            </div>
          </div>
          <div className="space-y-3 rounded-2xl bg-slate-50 p-4">
            <label className="flex items-center gap-3">
              <input data-testid="form-mostrar-estoque" type="checkbox" checked={form.mostrarEstoque} onChange={(e) => setForm({ ...form, mostrarEstoque: e.target.checked })} className="h-5 w-5 accent-mk-blue" />
              <span className="text-sm font-bold text-slate-800">Exibir quantidade em estoque para o cliente</span>
            </label>
            <p className="text-xs text-slate-400">Quando ativado, o cliente vê apenas o total das duas lojas (ex.: "8 unidades em estoque"). Nunca os saldos separados.</p>
            <label className="flex items-center gap-3">
              <input data-testid="form-destaque" type="checkbox" checked={form.destaque} onChange={(e) => setForm({ ...form, destaque: e.target.checked })} className="h-5 w-5 accent-mk-blue" />
              <span className="text-sm font-bold text-slate-800">Destacar na página inicial</span>
            </label>
            <p className="text-xs text-slate-400">O destaque entra no rascunho da página inicial — publique em "Produtos em destaque" para exibir.</p>
          </div>
        </div>

        <div className="rounded-2xl bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <label className={labelCls}>Fotos do produto *</label>
            <button
              data-testid="form-upload-button"
              type="button"
              disabled={subindo}
              onClick={() => fileRef.current?.click()}
              className="inline-flex items-center gap-2 rounded-full bg-mk-blue px-4 py-2 text-xs font-bold text-white hover:bg-mk-bluelight disabled:opacity-60"
            >
              <Upload size={14} /> {subindo ? 'Enviando...' : 'Adicionar fotos'}
            </button>
            <input ref={fileRef} data-testid="form-file-input" type="file" accept="image/jpeg,image/png,image/webp" multiple hidden onChange={(e) => enviarArquivos(e.target.files)} />
          </div>
          {fotos.length > 0 ? (
            <div className="mt-3 grid grid-cols-3 gap-3 sm:grid-cols-4">
              {fotos.map((url, i) => (
                <div key={url + i} data-testid={`form-foto-${i}`} className={`relative overflow-hidden rounded-xl border-2 ${i === 0 ? 'border-mk-green' : 'border-slate-200'}`}>
                  <img src={url} alt={`Foto ${i + 1}`} className="aspect-square w-full object-cover" />
                  {i === 0 && <span className="absolute left-1 top-1 rounded-full bg-mk-green px-2 py-0.5 text-[9px] font-extrabold uppercase text-white">Principal</span>}
                  <div className="absolute bottom-1 right-1 flex gap-1">
                    {i !== 0 && <button type="button" data-testid={`form-foto-principal-${i}`} onClick={() => principal(i)} aria-label="Definir como principal" className="rounded-full bg-white/95 p-1.5 text-mk-green shadow"><Star size={13} /></button>}
                    {i !== 0 && <button type="button" onClick={() => mover(i, -1)} aria-label="Mover para esquerda" className="rounded-full bg-white/95 p-1.5 text-slate-600 shadow"><ChevronLeft size={13} /></button>}
                    {i !== fotos.length - 1 && <button type="button" onClick={() => mover(i, 1)} aria-label="Mover para direita" className="rounded-full bg-white/95 p-1.5 text-slate-600 shadow"><ChevronRight size={13} /></button>}
                    <button type="button" data-testid={`form-foto-remover-${i}`} onClick={() => setFotos(fotos.filter((_, j) => j !== i))} aria-label="Remover foto" className="rounded-full bg-white/95 p-1.5 text-mk-red shadow"><Trash2 size={13} /></button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="mt-3 rounded-xl border border-dashed border-slate-300 py-8 text-center text-sm text-slate-400">
              Nenhuma foto ainda. JPG, PNG ou WebP até 5MB.
            </p>
          )}
        </div>

        <div className="flex flex-wrap gap-3">
          <button data-testid="form-save-button" type="submit" disabled={enviando} className="inline-flex items-center gap-2 rounded-full bg-mk-red px-6 py-3 text-sm font-bold text-white shadow-md hover:bg-mk-redbright disabled:opacity-60">
            <Save size={16} /> {enviando ? 'Salvando...' : 'Salvar produto'}
          </button>
          <button data-testid="form-cancel-button" type="button" onClick={() => navigate('/admin/produtos')} className="rounded-full border-2 border-slate-300 px-6 py-3 text-sm font-bold text-slate-600 hover:bg-slate-50">
            Cancelar
          </button>
        </div>
      </form>
    </div>
  );
}
